import { PLANS, type PlanId } from '@shared/plans';
import { CloudError } from '@/cloud/ApiClient';
import { ProjectStore } from '@/persistence/ProjectStore';
import type { ProjectStorage, StoredProjectSummary } from '@/persistence/ProjectStorage';
import {
  DEFAULT_PROJECT_NAME,
  identityOf,
  isEmptyProject,
  newProjectIdentity,
  projectFileName,
  summarizeProject,
  uniqueProjectName,
  type LandscapeProject,
  type ProjectIdentity,
  type ProjectLoadReport,
} from '@/project/Project';
import {
  ProjectFormatError,
  parseProjectJson,
  toProjectJson,
} from '@/project/ProjectSerializer';
import { AutosaveService, type AutosaveStatus } from '@/project/AutosaveService';
import { UnloadGuard } from '@/project/UnloadGuard';
import {
  GENERATED_TEMPLATE_IDS,
  MAX_SITE_METERS,
  MIN_SITE_METERS,
  clampSize,
  defaultNewSiteOptions,
  randomSiteSeed,
  type NewSiteKind,
  type NewSiteOptions,
} from '@/project/NewSite';
import { getTerrainTemplate } from '@/terrain/TerrainTemplates';
import type { TerrainTemplateId } from '@/terrain/TerrainTypes';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import { clear, el, formatLength } from './dom';
import { icon, type IconName } from './Icons';
import { PipelineInspector } from './PipelineInspector';
import {
  Dialog,
  dialogButton,
  dialogField,
  dialogSection,
  dialogSegment,
  numberInput,
  textInput,
} from './Dialog';

/**
 * The Project group of the toolbar: New site, Open, Save, Save as, Rename.
 *
 * The menu owns the *identity* of the document being edited — id, name, creation date — which is
 * what makes Save behave the way users expect: the first save asks for a name, every save after it
 * silently updates that same record, and opening a site adopts its identity so editing it and
 * pressing Ctrl+S does not fork a copy. Save as forks deliberately, under a new id; Rename changes
 * the name of the record in place and leaves everything else where it is.
 *
 * It also owns **autosave** (milestone 7.3), for the same reason: autosaving is just committing to
 * that identity on a timer. A site that has never been saved gets a provisional identity on its
 * first edit, so work is never held hostage to naming the document — but it stays *unnamed*, and an
 * explicit Save still opens the naming dialog, which then renames the draft record in place rather
 * than forking a second copy of it.
 *
 * Two rules keep that from filling the library with rubbish. An unnamed draft is only written once
 * there is something in it (`isEmptyProject`), so booting the app and moving the sun leaves nothing
 * behind; and the placeholder name is indexed against what is already stored
 * (`uniqueProjectName`), so a second draft is "Untitled site 2" rather than a second "Untitled
 * site". The same indexing runs on every explicit name, which is why two records can never share
 * one.
 *
 * All state that describes the site itself lives in the app (terrain, world, environment); this
 * class only asks for a snapshot and hands snapshots back.
 */

/** New-site starting points offered by the dialog. `photo` is not a terrain template. */
type NewSiteChoice = NewSiteKind | 'photo';

/**
 * What the name dialog does with the name it collects.
 *
 * `save` names the document being edited — the first save, and the one that turns an autosaved draft
 * into a named site. `copy` forks it into a second record. `rename` changes the name of the record
 * in place. All three end in the same write; only the identity they write under differs.
 */
type NameDialogMode = 'save' | 'copy' | 'rename';

export interface ProjectMenuDeps {
  /** Element to mount dialogs and toasts into — the HUD root. */
  host: HTMLElement;
  /** The plant catalog, used to build the reference prompt and compile a plan. */
  catalog?: () => FloraCatalog;
  /** Snapshot the running site as a project document. */
  captureProject: (identity: ProjectIdentity) => LandscapeProject;
  /** Rebuild the running site from a project document. */
  applyProject: (project: LandscapeProject) => ProjectLoadReport;
  /**
   * Replace the site with a fresh blank or generated one. Returns a load report when the template
   * built a whole site rather than only terrain, so the confirmation can say what it made.
   */
  newSite: (options: NewSiteOptions) => ProjectLoadReport | null;
  /** Plants currently placed, used to warn before a destructive replace. */
  plantCount: () => number;
  /** Notified whenever the autosave phase changes, for the status bar's save indicator. */
  onAutosaveStatus?: (status: AutosaveStatus) => void;
  /** Notified whenever the document's name changes, so the status bar follows a rename. */
  onDocumentName?: (name: string) => void;
  /** Autosave debounce overrides (tests use a short one). */
  autosave?: { debounceMs?: number; maxWaitMs?: number };
  /**
   * Where sites are kept. Defaults to this device's IndexedDB.
   *
   * Injected both by tests and by the app when a user is signed in, in which case it is the
   * account-backed store — every flow in this class is written against the interface, so neither
   * knows which it has.
   */
  store?: ProjectStorage;
  /** Shown on the library screen so the user knows where a save is going. Null when local-only. */
  signedInAs?: () => string | null;
  /**
   * The plan the account is on, for the saved-site ceiling.
   *
   * Null when there is no account, which is also the answer for local storage: sites in this
   * browser are not counted against anything, and never have been. Read per call rather than
   * captured, so signing in mid-session moves the library's count without rebuilding the menu.
   */
  plan?: () => PlanId | null;
}

export class ProjectMenu {
  readonly store: ProjectStorage;
  readonly autosave: AutosaveService;

  private identity: ProjectIdentity | null = null;
  /**
   * Whether the user has named this document. A draft autosaved under the placeholder name has an
   * identity but is not named, so Ctrl+S still offers to name it.
   */
  private named = false;
  private readonly unloadGuard: UnloadGuard;
  private dialog: Dialog | null = null;
  private toast: HTMLElement | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The last refusal an autosave was told about, so a full library is announced once rather than
   * every time the debounce elapses. Cleared by the first save that succeeds.
   */
  private announced: string | null = null;

  constructor(private readonly deps: ProjectMenuDeps) {
    this.store = deps.store ?? new ProjectStore();
    this.autosave = new AutosaveService({
      // An unnamed draft with nothing in it is not a document yet — see `isEmptyDraft`.
      shouldSave: () => this.identity !== null || !this.isEmptyDraft(),
      save: async () => this.commit(await this.ensureIdentity(), { silent: true }),
      ...(deps.autosave?.debounceMs !== undefined ? { debounceMs: deps.autosave.debounceMs } : {}),
      ...(deps.autosave?.maxWaitMs !== undefined ? { maxWaitMs: deps.autosave.maxWaitMs } : {}),
      onStatus: (status) => deps.onAutosaveStatus?.(status),
    });
    this.unloadGuard = new UnloadGuard({
      hasUnsavedChanges: () => this.hasUnsavedWork,
      onFlush: () => void this.autosave.flush(),
    });
  }

  /** Name of the document being edited, or the placeholder when it has never been saved. */
  get documentName(): string {
    return this.identity?.name ?? DEFAULT_PROJECT_NAME;
  }

  /**
   * The stable id of the document being edited, or null for a draft that has never been written.
   *
   * Read by anything that has to recognise *this site* across a reload — the shots gallery, which
   * only offers to re-stage a frame onto the site it was taken of. Null is not a gap to paper over:
   * a draft has no identity yet, and the first autosave mints one (see {@link ensureIdentity}).
   */
  get documentId(): string | null {
    return this.identity?.id ?? null;
  }

  /** Whether the current site has ever been saved or opened. */
  get isSaved(): boolean {
    return this.identity !== null;
  }

  /**
   * Whether navigating away now would lose work.
   *
   * Pending or in-flight changes count, and so does *everything* on a non-persistent store: when
   * IndexedDB is blocked, saves live in memory for the session only, so a reload discards a site
   * that the UI has been calling saved. That is precisely the case worth interrupting a navigation
   * for.
   */
  get hasUnsavedWork(): boolean {
    if (this.autosave.hasUnsavedChanges) return true;
    return !this.store.persistent && (this.identity !== null || this.deps.plantCount() > 0);
  }

  /**
   * The site model changed. Wired to every editor and environment mutation by the app; the service
   * collapses the burst into one write.
   */
  markDirty(): void {
    this.autosave.markDirty();
  }

  /**
   * Take over a site the editor is already showing, after the store beneath it changed.
   *
   * Marked dirty deliberately: the new store has never seen this document, so it is unsaved *there*
   * whatever its history in the old one. The first autosave then writes it to the right place.
   */
  adoptRunningDocument(project: LandscapeProject): void {
    this.setIdentity(null, false);
    this.deps.onDocumentName?.(project.name);
    this.autosave.markDirty();
  }

  /** The document was replaced by one that is already stored — nothing pending belongs to it. */
  private adopt(identity: ProjectIdentity | null, options: { named: boolean; dirty: boolean }): void {
    this.setIdentity(identity, options.named);
    if (options.dirty) this.autosave.markDirty();
    else this.autosave.markClean();
  }

  /** Adopt an identity and announce the name, so a rename reaches the status bar immediately. */
  private setIdentity(identity: ProjectIdentity | null, named: boolean): void {
    this.identity = identity;
    this.named = named;
    this.deps.onDocumentName?.(this.documentName);
  }

  /**
   * The identity autosave writes to, minting a provisional one for a site never saved by hand.
   *
   * The placeholder name is indexed against the library rather than used bare: every unnamed draft
   * would otherwise be called the same thing, which is exactly the case where a collision is the
   * norm. Asking the store is why this is async — the caller is an autosave, so the wait costs
   * nothing anyone is watching.
   */
  private async ensureIdentity(): Promise<ProjectIdentity> {
    const existing = this.identity;
    if (existing) return existing;
    const identity = newProjectIdentity(
      uniqueProjectName(DEFAULT_PROJECT_NAME, await this.storedNames()),
    );
    this.setIdentity(identity, false);
    return identity;
  }

  /**
   * Whether the site as it stands is an unnamed draft with nothing in it.
   *
   * Costs a snapshot, which is why it is only ever asked of a document that has no record yet: once
   * one exists the answer stops mattering — that record has to be kept up to date whatever is left
   * in it, including a site the user has just emptied.
   */
  private isEmptyDraft(): boolean {
    return isEmptyProject(this.deps.captureProject(newProjectIdentity()));
  }

  /** Names already in the library, optionally minus one record's own — for {@link uniqueProjectName}. */
  private async storedNames(exceptId?: string): Promise<string[]> {
    try {
      const projects = await this.store.summaries();
      return projects.filter((project) => project.id !== exceptId).map((project) => project.name);
    } catch (error) {
      // A library that cannot be read is no reason to refuse the save; the worst case is a duplicate.
      console.warn('Could not read the site library to check the name for duplicates.', error);
      return [];
    }
  }

  /* ---------------------------------- New site ---------------------------------- */

  openNewSite(): void {
    const options = defaultNewSiteOptions();
    let choice: NewSiteChoice = 'blank';

    const dialog = this.openDialog({
      title: 'New site',
      icon: 'new',
      subtitle: 'Rebuild a real place from imagery, start blank, or generate a landscape',
    });

    const create = (): void => {
      if (choice === 'photo') {
        const catalog = this.deps.catalog?.();
        if (!catalog) return;
        // The inspector is a full surface, not a dialog body, so entering it closes this one.
        dialog.close();
        this.openPipeline(catalog);
        return;
      }
      const report = this.deps.newSite(options);
      // A fresh site is not worth a record until it is edited — otherwise clicking New three times
      // would leave three empty drafts in the library.
      this.adopt(null, { named: false, dirty: false });
      dialog.close();
      if (options.kind === 'blank') {
        this.notify('New blank site');
      } else if (report) {
        // A furnished template built a whole site, so say what it built rather than just naming it.
        this.notify(
          `New ${getTerrainTemplate(options.templateId).name.toLowerCase()} · seed ${options.seed} · ` +
            `${report.plantCount} plants · ${report.siteObjectCount} built`,
        );
      } else {
        this.notify(
          `New ${getTerrainTemplate(options.templateId).name.toLowerCase()} · seed ${options.seed}`,
        );
      }
    };

    const adoptTemplateSize = (templateId: TerrainTemplateId): void => {
      const defaults = getTerrainTemplate(templateId).defaults;
      options.widthMeters = defaults.widthMeters;
      options.lengthMeters = defaults.lengthMeters;
    };

    const render = (): void => {
      clear(dialog.body);

      dialog.body.appendChild(
        dialogSegment(
          'Site type',
          // Imagery first: it is the only starting point that reproduces somewhere real, and the
          // one the rest of the app is built around. It is not the *selected* one, because Enter on
          // a freshly opened dialog should still create a site rather than launch a five-call run.
          [
            { id: 'photo', label: 'From satellite image' },
            { id: 'blank', label: 'Blank site' },
            { id: 'generated', label: 'Generated site' },
          ],
          choice,
          (id) => {
            choice = id as NewSiteChoice;
            if (choice !== 'photo') {
              options.kind = choice;
              // Adopt the size that belongs to the newly chosen starting point, so the dialog never
              // shows a generated template next to the blank canvas's dimensions.
              adoptTemplateSize(choice === 'blank' ? 'blank-custom' : options.templateId);
            }
            render();
            // The footer belongs to the choice as much as the body does: the photo path hands off to
            // another surface rather than creating anything, and a button still labelled "Create
            // site" is a promise the dialog does not keep.
            setActions();
          },
        ),
      );

      if (choice === 'photo') {
        this.renderPhotoChoice(dialog);
        return;
      }

      dialog.body.appendChild(
        el('p', {
          class: 'lg-hint',
          text:
            options.kind === 'blank'
              ? 'A flat, empty plot at the size you choose.'
              : 'Seeded procedural terrain from a template.',
        }),
      );

      if (options.kind === 'generated') {
        dialog.body.appendChild(dialogSection('Template'));
        const chips = el('div', { class: 'lg-chips' });
        for (const templateId of GENERATED_TEMPLATE_IDS) {
          const template = getTerrainTemplate(templateId);
          chips.appendChild(
            el('button', {
              class: `lg-chip${options.templateId === templateId ? ' is-active' : ''}`,
              text: template.name,
              title: template.description,
              attrs: {
                type: 'button',
                'aria-pressed': options.templateId === templateId,
                'data-autofocus': 'skip',
              },
              on: {
                click: () => {
                  options.templateId = templateId;
                  adoptTemplateSize(templateId);
                  render();
                },
              },
            }),
          );
        }
        dialog.body.appendChild(chips);

        const seedField = numberInput({
          value: options.seed,
          min: 1,
          step: 1,
          ariaLabel: 'Terrain seed',
          onChange: (value) => {
            options.seed = Math.max(1, Math.trunc(value) || 1);
          },
        });
        dialog.body.appendChild(
          dialogField(
            'Seed',
            el(
              'div',
              { class: 'lg-dialog__inline' },
              seedField,
              dialogButton({
                label: 'Randomize',
                icon: 'rotate',
                title: 'Roll a new terrain seed',
                onClick: () => {
                  options.seed = randomSiteSeed();
                  seedField.value = String(options.seed);
                },
              }),
            ),
            'The same seed and settings always produce the same terrain.',
          ),
        );
      }

      const sizeHint = el('span', {
        class: 'lg-dialog__hint',
        text: describeSize(options.widthMeters, options.lengthMeters),
      });

      dialog.body.appendChild(dialogSection('Size'));
      dialog.body.appendChild(
        dialogField(
          'Width',
          numberInput({
            value: round1(options.widthMeters),
            min: MIN_SITE_METERS,
            max: MAX_SITE_METERS,
            step: 1,
            ariaLabel: 'Site width in meters',
            onChange: (value) => {
              options.widthMeters = clampSize(value);
              sizeHint.textContent = describeSize(options.widthMeters, options.lengthMeters);
            },
          }),
        ),
      );
      dialog.body.appendChild(
        dialogField(
          'Length',
          numberInput({
            value: round1(options.lengthMeters),
            min: MIN_SITE_METERS,
            max: MAX_SITE_METERS,
            step: 1,
            ariaLabel: 'Site length in meters',
            onChange: (value) => {
              options.lengthMeters = clampSize(value);
              sizeHint.textContent = describeSize(options.widthMeters, options.lengthMeters);
            },
          }),
        ),
      );
      dialog.body.appendChild(sizeHint);

      const plants = this.deps.plantCount();
      if (plants > 0) {
        dialog.body.appendChild(
          el(
            'p',
            { class: 'lg-dialog__warning' },
            el('span', { class: 'lg-dialog__warnicon', html: icon('warning', 15) }),
            el('span', {
              text:
                `This replaces the current site — ${plants} plant${plants === 1 ? '' : 's'} ` +
                'and its undo history will be discarded. Save first to keep them.',
            }),
          ),
        );
      }
    };

    const setActions = (): void => {
      const staged = choice === 'photo';
      dialog.setActions(
        dialogButton({ label: 'Cancel', onClick: () => dialog.close() }),
        dialogButton({
          label: staged ? 'Open the pipeline' : 'Create site',
          primary: true,
          icon: staged ? 'layers' : 'new',
          onClick: create,
          // The staged path creates nothing from here — it hands off to a surface with its own
          // Create button — so it is available the moment the mode is chosen.
          disabled: staged && this.deps.catalog?.() === undefined,
        }),
      );
    };

    render();
    setActions();
    dialog.show();
  }

  /**
   * The "From satellite image" body: one line, because it is a tab and not a manual.
   *
   * It used to carry the whole method — five stages named, every intermediate listed, the argument
   * for staging over one call. All of that was true and none of it belonged here: the reader has
   * not chosen yet, and a paragraph at the moment of choosing is read as work rather than as
   * information. The pipeline explains itself once it is open, where each part of it is visible.
   */
  private renderPhotoChoice(dialog: Dialog): void {
    if (this.deps.catalog?.() === undefined) {
      dialog.body.appendChild(
        el('p', {
          class: 'lg-hint',
          text: 'Reference reconstruction is unavailable — the plant catalog has not loaded.',
        }),
      );
      return;
    }

    dialog.body.appendChild(
      el('p', {
        class: 'lg-hint',
        text: 'Cut a square from the map, or drop in your own photograph, and get the real terrain, surfaces, planting and buildings back.',
      }),
    );
  }

  /**
   * Open the staged pipeline inspector.
   *
   * A full surface rather than a dialog: it shows five stages, a photograph, and an artifact at
   * once, and the New Site dialog would have to become a window to hold it. Only one is ever up, and
   * it owns its own photographs — the two paths do not share state, because the staged run wants to
   * be started deliberately rather than inherited half-configured.
   */
  openPipeline(catalog: FloraCatalog): PipelineInspector {
    this.pipeline?.dispose();
    const inspector = new PipelineInspector({
      host: this.deps.host,
      catalog,
      onCreate: (project) => {
        const report = this.deps.applyProject(project);
        this.adopt(null, { named: false, dirty: true });
        inspector.dispose();
        this.pipeline = null;
        this.notify(
          `Built “${project.name}” from reference · ${report.plantCount} plants · ${report.siteObjectCount} built`,
        );
      },
      onClose: () => {
        this.pipeline = null;
      },
    });
    this.pipeline = inspector;
    return inspector;
  }

  private pipeline: PipelineInspector | null = null;

  /* ------------------------------------ Save ------------------------------------ */

  /** Toolbar Save / Ctrl+S: quick-save a named document, otherwise ask for a name first. */
  save(): void {
    if (this.identity && this.named) {
      void this.commit(this.identity);
      return;
    }
    this.openNameDialog('save');
  }

  /**
   * Save as… — write the site as a *new* record and keep editing that one.
   *
   * A document that has never been saved has nothing to fork, so this is its first save instead:
   * offering to copy a site into a second record when the first does not exist yet would leave the
   * user with one file either way and a dialog that lied about which.
   */
  openSaveAs(): void {
    this.openNameDialog(this.identity ? 'copy' : 'save');
  }

  /** Rename… — change the name of the record in place, keeping its id, history, and content. */
  openRename(): void {
    this.openNameDialog(this.identity ? 'rename' : 'save');
  }

  /**
   * The one dialog behind Save, Save as, and Rename: they differ only in what they do with the name.
   *
   * The taken names are fetched once the dialog is up rather than before it, so a slow store never
   * delays the field appearing — the hint fills in a moment later, and the same check runs again at
   * submit time regardless, which is what actually guarantees the name is free.
   */
  private openNameDialog(mode: NameDialogMode): void {
    const copying = mode === 'copy';
    const renaming = mode === 'rename';
    let name = this.documentName;
    let taken: string[] = [];

    const dialog = this.openDialog({
      title: copying ? 'Save a copy' : renaming ? 'Rename site' : 'Save site',
      icon: renaming ? 'rename' : 'save',
      subtitle: copying
        ? 'Writes a second site and continues in it'
        : renaming
          ? `Renames “${this.documentName}” — the site itself is untouched`
          : 'Stored in this browser',
    });

    const submit = (): void => {
      dialog.close();
      void this.applyName(name, mode);
    };

    const collision = el('p', { class: 'lg-dialog__hint', attrs: { hidden: true } });
    const syncCollision = (): void => {
      const desired = name.trim() || DEFAULT_PROJECT_NAME;
      const resolved = uniqueProjectName(desired, taken);
      collision.hidden = resolved === desired;
      collision.textContent =
        resolved === desired
          ? ''
          : `A site called “${desired}” already exists, so this one is saved as “${resolved}”.`;
    };

    dialog.body.appendChild(
      dialogField(
        'Name',
        textInput({
          value: name,
          ariaLabel: 'Site name',
          placeholder: DEFAULT_PROJECT_NAME,
          onInput: (value) => {
            name = value;
            syncCollision();
          },
          onSubmit: submit,
        }),
      ),
    );
    dialog.body.appendChild(collision);
    if (!renaming) dialog.body.appendChild(this.summaryCard());
    dialog.body.appendChild(
      el('p', {
        class: 'lg-hint',
        text: renaming
          ? 'Only the name changes. The site keeps its place in the library, and autosave carries on writing to it.'
          : this.whereSavesGo(),
      }),
    );

    dialog.setActions(
      dialogButton({ label: 'Cancel', onClick: () => dialog.close() }),
      ...(renaming
        ? []
        : [
            dialogButton({
              label: 'Download file',
              icon: 'download',
              title: 'Export a .landscape.json file',
              onClick: () => this.download(name),
            }),
          ]),
      dialogButton({
        label: renaming ? 'Rename' : copying ? 'Save copy' : 'Save',
        primary: true,
        icon: renaming ? 'rename' : 'save',
        onClick: submit,
      }),
    );
    dialog.show();

    void this.storedNames(copying ? undefined : this.identity?.id).then((names) => {
      taken = names;
      syncCollision();
    });
  }

  /**
   * Give the document the name the dialog collected, and write it.
   *
   * The name is resolved against the library *here* rather than in the dialog, because this is the
   * last moment before the write — and it excludes the record's own name for everything but a copy,
   * so re-saving a site under the name it already has is not treated as a clash with itself.
   */
  private async applyName(desired: string, mode: NameDialogMode): Promise<void> {
    const current = this.identity;
    const forking = mode === 'copy' || current === null;
    const name = uniqueProjectName(
      desired,
      await this.storedNames(forking ? undefined : current?.id),
    );
    // A copy is a new record; naming a draft renames the one autosave already created.
    const identity = current && !forking ? { ...current, name } : newProjectIdentity(name);
    this.setIdentity(identity, true);
    await this.commit(identity, { verb: mode === 'rename' ? 'Renamed to' : 'Saved' });
  }

  /* ------------------------------------ Open ------------------------------------ */

  openLibrary(): void {
    const dialog = this.openDialog({
      title: 'Open site',
      icon: 'open',
      subtitle: this.libraryLine(null),
    });

    const list = el('div', { class: 'lg-doclist' });
    dialog.body.appendChild(list);

    const refresh = async (): Promise<void> => {
      clear(list);
      list.appendChild(el('p', { class: 'lg-empty', text: 'Loading saved sites…' }));
      const projects = await this.store.summaries();
      clear(list);
      dialog.setSubtitle(this.libraryLine(projects.length));

      if (projects.length === 0) {
        list.appendChild(
          el('p', {
            class: 'lg-empty',
            text: 'No saved sites yet. Save the current site, or import a .landscape.json file.',
          }),
        );
        return;
      }
      for (const project of projects) {
        list.appendChild(this.documentRow(project, dialog, refresh));
      }
    };

    dialog.setActions(
      dialogButton({ label: 'Close', onClick: () => dialog.close() }),
      dialogButton({
        label: 'Import file…',
        icon: 'open',
        title: 'Open a .landscape.json file',
        onClick: () => this.importFile(dialog),
      }),
      dialogButton({
        label: 'Export current',
        icon: 'download',
        title: 'Download the current site as .landscape.json',
        onClick: () => this.download(this.documentName),
      }),
    );
    dialog.show();
    void refresh();
  }

  /**
   * One row of the library, built from a summary rather than from a document.
   *
   * The counts come from the store's summary — computed on the spot for local storage, recorded at
   * save time for an account — so listing thirty sites costs one request instead of thirty
   * documents. The document itself is fetched only when the row is actually opened.
   */
  private documentRow(
    project: StoredProjectSummary,
    dialog: Dialog,
    refresh: () => Promise<void>,
  ): HTMLElement {
    const summary = project.stats;
    const current = this.identity?.id === project.id;
    // `stats` is null only for a record stored before counts were kept. Showing the date alone
    // beats showing "0 plants", which would read as a corrupted save.
    const meta =
      summary === null
        ? formatDate(project.updatedAt)
        : `${summary.plantCount} plant${summary.plantCount === 1 ? '' : 's'} · ` +
          `${summary.speciesCount} species · ${getTerrainTemplate(summary.templateId).name} · ` +
          formatDate(project.updatedAt);

    const row = el(
      'div',
      { class: `lg-doc${current ? ' is-current' : ''}` },
      el(
        'button',
        {
          class: 'lg-doc__open',
          attrs: { type: 'button', 'data-autofocus': 'skip' },
          title: 'Open this site',
          on: { click: () => void this.openStored(project, dialog) },
        },
        el('span', { class: 'lg-doc__name', text: project.name }),
        el('span', { class: 'lg-doc__meta', text: meta }),
      ),
      el('button', {
        class: 'lg-iconbtn',
        html: icon('rename', 15),
        title: `Rename “${project.name}”`,
        attrs: { type: 'button', 'aria-label': `Rename ${project.name}`, 'data-autofocus': 'skip' },
        on: { click: () => this.editRowName(row, project, meta, refresh) },
      }),
      el('button', {
        class: 'lg-iconbtn lg-iconbtn--danger',
        html: icon('delete', 15),
        title: `Delete “${project.name}”`,
        attrs: { type: 'button', 'aria-label': `Delete ${project.name}`, 'data-autofocus': 'skip' },
        on: {
          click: () => {
            void this.store.remove(project.id).then(async () => {
              // Deleting the open document only forgets where it is stored; the site stays loaded.
              // Autosave is stood down with it, so the next edit starts a new draft instead of
              // resurrecting the record the user just deleted.
              if (this.identity?.id === project.id) {
                this.adopt(null, { named: false, dirty: false });
              }
              this.notify(`Deleted “${project.name}”`);
              await refresh();
            });
          },
        },
      }),
    );
    return row;
  }

  /**
   * Rename a stored site from its row in the Open dialog.
   *
   * Edited in place rather than in a second dialog: only one project dialog is ever up, so a rename
   * prompt would have to close the library the user is standing in — and this is the screen where
   * renaming is actually wanted, because it is where a shelf of near-identical drafts is visible.
   *
   * Only the name is rewritten — the store is asked to rename, not to store a document this screen
   * is holding. That is what keeps renaming a site you do not have open from overwriting it with
   * the one you do, and it means the library never has to load a document to change its label.
   */
  private editRowName(
    row: HTMLElement,
    project: StoredProjectSummary,
    meta: string,
    refresh: () => Promise<void>,
  ): void {
    let name = project.name;
    let settled = false;

    const commitName = (): void => {
      if (settled) return;
      settled = true;
      void (async () => {
        const desired = name.trim() || DEFAULT_PROJECT_NAME;
        if (desired === project.name) {
          await refresh();
          return;
        }
        const resolved = uniqueProjectName(desired, await this.storedNames(project.id));
        await this.store.rename(project.id, resolved);
        // The open document may be the one just renamed, so its identity has to follow.
        if (this.identity?.id === project.id) {
          this.setIdentity({ ...this.identity, name: resolved }, this.named);
        }
        this.notify(`Renamed to “${resolved}”`);
        await refresh();
      })();
    };

    const cancel = (): void => {
      if (settled) return;
      settled = true;
      void refresh();
    };

    const input = textInput({
      value: project.name,
      ariaLabel: `New name for ${project.name}`,
      placeholder: DEFAULT_PROJECT_NAME,
      onInput: (value) => {
        name = value;
      },
      onSubmit: commitName,
    });
    // Escape has to be caught here: the dialog would otherwise take it and close the whole library.
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      cancel();
    });
    input.addEventListener('blur', commitName);

    clear(row);
    row.appendChild(
      el(
        'div',
        { class: 'lg-doc__open lg-doc__rename' },
        input,
        el('span', { class: 'lg-doc__meta', text: meta }),
      ),
    );
    row.appendChild(
      dialogButton({ label: 'Rename', primary: true, icon: 'rename', onClick: commitName }),
    );
    input.focus();
    input.select();
  }

  /**
   * The sentence under the Save dialog explaining where the file is going.
   *
   * Four states, and each is worth saying plainly: an account (it follows you to another machine),
   * this browser only (it does not), an expired session, and blocked storage. Getting this wrong
   * is how a user loses a day's work believing it was safe somewhere it was not.
   */
  private libraryLine(count: number | null): string {
    return describeLibrary(this.deps.plan?.() ?? null, count);
  }

  private whereSavesGo(): string {
    const account = this.deps.signedInAs?.() ?? null;
    if (account !== null) {
      return this.store.persistent
        ? `Saved to your account (${account}), so this site opens on any machine you sign in on. ` +
            'Download a .landscape.json file to keep a copy of your own.'
        : 'Your session has expired, so this save will not reach your account. Sign in again, or ' +
            'download a .landscape.json file to keep it.';
    }
    return this.store.persistent
      ? 'Saved sites live in this browser and reopen from the Open dialog. Download a .landscape.json file to keep a copy elsewhere.'
      : 'Browser storage is unavailable, so this save will not survive a reload. Download a .landscape.json file to keep it.';
  }

  /* ----------------------------------- Plumbing ---------------------------------- */

  /**
   * Fetch a stored document and open it.
   *
   * The library holds summaries, so opening is the moment the document is actually read. Against
   * IndexedDB that is instant; against an account it is a request that can fail or find nothing —
   * a site deleted in another tab, for instance — so both outcomes are reported rather than
   * leaving a row that does nothing when clicked.
   */
  private async openStored(project: StoredProjectSummary, dialog: Dialog): Promise<void> {
    try {
      const document = await this.store.get(project.id);
      if (!document) {
        this.notify(`“${project.name}” is no longer in the library.`);
        return;
      }
      this.load(document, dialog);
    } catch (error) {
      console.warn('Could not open the saved site.', error);
      this.notify(
        error instanceof Error ? `Could not open that site — ${error.message}` : 'Could not open that site.',
      );
    }
  }

  /**
   * Open the site this session should land on, rather than the blank canvas.
   *
   * Two candidates, in this order. **The site the user last had open** — asked of the store, so an
   * account answers across machines and a standalone build answers for this browser. Failing that,
   * `starterId`: the library site the server nominates for someone who has never been here. A
   * returning user is resumed; only a genuinely new one is introduced.
   *
   * Guarded on {@link isEmptyDraft} rather than trusted to be called at the right moment. The call
   * site is a network response, so by the time it arrives the user has had a few seconds in which
   * they were perfectly entitled to start planting — and replacing what someone has just drawn with
   * a stored site is a far worse experience than the blank canvas this is trying to avoid. Same
   * reasoning for a document that already has an identity: that is a site they opened themselves,
   * and it wins.
   *
   * Returns whether a site was opened, which is what makes it testable without a DOM.
   */
  async openOpeningSite(starterId: string | null = null): Promise<boolean> {
    if (this.identity !== null || !this.isEmptyDraft()) return false;
    try {
      const id = (await this.store.resumeSite()) ?? starterId;
      if (id === null) return false;
      const document = await this.store.get(id);
      if (!document) return false;
      // Still empty? The awaits above are another window in which the user may have started work.
      if (this.identity !== null || !this.isEmptyDraft()) return false;
      this.load(document, null);
      return true;
    } catch (error) {
      // An opening courtesy that failed is not worth a message; the canvas is behind it either way.
      console.warn('Could not open the site to resume.', error);
      return false;
    }
  }

  /**
   * Record which site is open, without letting that bookkeeping affect anything the user sees.
   *
   * Every caller is `void`-ed: the pointer is a convenience for the *next* session, and there is no
   * version of "could not record which site you have open" worth interrupting this one for. The
   * stores promise not to throw; this is the belt to that braces.
   */
  private async noteOpened(id: string): Promise<void> {
    try {
      await this.store.markOpened(id);
    } catch (error) {
      console.warn('Could not record which site is open.', error);
    }
  }

  /**
   * Adopt a document into the editor. `stored` distinguishes opening a record that already exists in
   * the library — nothing to write until it is edited — from importing a file, which is content the
   * library does not have yet and should autosave immediately.
   */
  private load(project: LandscapeProject, dialog: Dialog | null, stored = true): void {
    const report = this.deps.applyProject(project);
    this.adopt(identityOf(project), { named: true, dirty: !stored });
    // Only a document the store already holds is worth resuming onto. An import has no record
    // there yet, and marking it here would point the next session at an id that does not exist;
    // its first autosave writes the row and stamps it through `noteOpened` below.
    if (stored) void this.noteOpened(project.id);
    dialog?.close();
    const skipped =
      report.skippedPlantCount > 0
        ? ` · ${report.skippedPlantCount} plant${report.skippedPlantCount === 1 ? '' : 's'} skipped (unknown species)`
        : '';
    const built = report.siteObjectCount > 0 ? ` · ${report.siteObjectCount} built` : '';
    this.notify(`Opened “${project.name}” · ${report.plantCount} plants${built}${skipped}`);
  }

  /**
   * Write the document. An explicit save reports what it did; an autosave stays quiet and lets the
   * status bar speak, and re-throws so the service knows the change is still unwritten.
   */
  private async commit(
    identity: ProjectIdentity,
    options: { silent?: boolean; verb?: string } = {},
  ): Promise<void> {
    // Snapshotting now is what makes it safe to call the document clean now: this write covers
    // every change up to this line, and anything edited while it is in flight re-dirties it.
    const snapshot = this.deps.captureProject(identity);
    if (!options.silent) this.autosave.markClean();
    try {
      const saved = await this.store.save(snapshot);
      this.announced = null;
      // The site just written is the one being worked in, so it is also the one to resume onto.
      // This is what carries a brand-new draft into the pointer: it had no store record when it
      // was adopted, and its first write is the moment it gains one.
      void this.noteOpened(saved.id);
      // Keep the name and creation date the store echoed back, but not `named`: a silent autosave
      // of an untitled draft must not make Ctrl+S stop asking for a name.
      this.setIdentity(identityOf(saved), this.named);
      if (options.silent) return;
      const verb = options.verb ?? 'Saved';
      this.notify(
        this.store.persistent
          ? `${verb} “${saved.name}”`
          : `${verb} “${saved.name}” — ${
              this.deps.signedInAs?.() != null
                ? 'not synced, your session has expired'
                : 'this session only, browser storage is unavailable'
            }`,
      );
    } catch (error) {
      console.error('Saving the site failed.', error);
      // A refusal the server has already written a sentence for — the library is full, the
      // document is over the size limit. Shown even on a silent autosave, and that exception is
      // the point: these are decisions the user has to act on, not blips that clear themselves,
      // and the autosave indicator's tooltip is not where somebody finds out their library is
      // full. Everything else stays quiet on the silent path, as before.
      const explained = error instanceof CloudError && error.explained ? error.message : null;
      if (options.silent) {
        // Once, not once per debounce: a user who keeps working after their library fills up would
        // otherwise be told about it every eight seconds, which turns the one message that matters
        // into the one they learn to dismiss without reading.
        if (explained !== null && explained !== this.announced) {
          this.announced = explained;
          this.notify(explained, 'error');
        }
        throw error;
      }
      this.announced = explained;
      // Nothing was written, so the changes go back on the pile for autosave to retry.
      this.autosave.markDirty();
      this.notify(explained ?? 'Could not save this site. See the console for details.', 'error');
    }
  }

  /** Download the current site as an editable `.landscape.json` file. */
  private download(name: string): void {
    const identity = this.identity ?? newProjectIdentity(name);
    const project = this.deps.captureProject({
      ...identity,
      name: name.trim() || DEFAULT_PROJECT_NAME,
    });
    const blob = new Blob([toProjectJson(project)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = el('a', {
      attrs: { href: url, download: projectFileName(project) },
      style: { display: 'none' },
    });
    this.deps.host.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    this.notify(`Exported ${projectFileName(project)}`);
  }

  private importFile(dialog: Dialog): void {
    const input = el('input', {
      attrs: { type: 'file', accept: '.json,application/json' },
      style: { display: 'none' },
      on: {
        change: () => {
          const file = input.files?.[0];
          if (!file) return;
          void file
            .text()
            .then((text) => {
              // An imported file is not in the library yet, so it autosaves into it on arrival.
              this.load(parseProjectJson(text), dialog, false);
            })
            .catch((error: unknown) => {
              const message =
                error instanceof ProjectFormatError
                  ? error.message
                  : 'That file could not be opened as a site.';
              this.notify(message, 'error');
            })
            .finally(() => input.remove());
        },
      },
    });
    this.deps.host.appendChild(input);
    input.click();
  }

  /** A short description of what a save would contain, so the dialog is not a bare text box. */
  private summaryCard(): HTMLElement {
    const project = this.deps.captureProject(this.identity ?? newProjectIdentity(this.documentName));
    const summary = summarizeProject(project);
    const template = getTerrainTemplate(summary.templateId);
    return el(
      'div',
      { class: 'lg-dialog__summary' },
      summaryRow('Plants', `${summary.plantCount} · ${summary.speciesCount} species`),
      summaryRow('Built', `${summary.siteObjectCount} buildings & paving`),
      summaryRow('Groups', String(summary.groupCount)),
      summaryRow('Site', `${template.name} · ${describeSize(summary.widthMeters, summary.lengthMeters)}`),
    );
  }

  /** Only one project dialog is ever up; opening another replaces it. */
  private openDialog(options: {
    title: string;
    icon: IconName;
    subtitle: string;
    onClose?: () => void;
  }): Dialog {
    this.dialog?.dispose();
    const dialog = new Dialog({
      host: this.deps.host,
      title: options.title,
      icon: options.icon,
      subtitle: options.subtitle,
      onClose: () => {
        options.onClose?.();
        if (this.dialog === dialog) this.dialog = null;
      },
    });
    this.dialog = dialog;
    return dialog;
  }

  /** Transient confirmation, so an action with no visible dialog still reports what it did. */
  notify(message: string, tone: 'info' | 'error' = 'info'): void {
    if (!this.toast) {
      this.toast = el('div', { class: 'lg-toast', attrs: { role: 'status' } });
      this.deps.host.appendChild(this.toast);
    }
    this.toast.textContent = message;
    this.toast.classList.toggle('is-error', tone === 'error');
    this.toast.classList.add('is-visible');
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toast?.classList.remove('is-visible');
    }, tone === 'error' ? 6000 : 2800);
  }

  dispose(): void {
    this.unloadGuard.dispose();
    this.autosave.dispose();
    this.pipeline?.dispose();
    this.pipeline = null;
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.toastTimer = null;
    this.dialog?.dispose();
    this.dialog = null;
    this.toast?.remove();
    this.toast = null;
    this.store.close();
  }
}

function summaryRow(label: string, value: string): HTMLElement {
  return el(
    'div',
    { class: 'lg-field' },
    el('span', { class: 'lg-field__label', text: label }),
    el('span', { class: 'lg-field__value', text: value }),
  );
}

function describeSize(widthMeters: number, lengthMeters: number): string {
  return (
    `${formatLength(widthMeters)} × ${formatLength(lengthMeters)}  ·  ` +
    `${formatLength(widthMeters, 'feet')} × ${formatLength(lengthMeters, 'feet')}`
  );
}

/**
 * The line above the library: how many sites are kept, where, and how many more will fit.
 *
 * The count is only a ceiling for an account — a plan is a thing an account has, and sites kept in
 * this browser have never been counted against anything. `null` for the count is the listing still
 * loading, because "0 of 3" flashing before the real number is worse than no number at all.
 */
export function describeLibrary(plan: PlanId | null, count: number | null): string {
  const where = plan === null ? 'Saved in this browser' : 'Saved to your account';
  if (count === null) return where;

  const sites = `${where} · ${String(count)} site${count === 1 ? '' : 's'}`;
  if (plan === null) return sites;

  const { projectLimit, name } = PLANS[plan];
  if (projectLimit === null) return sites;
  // Naming the plan is what makes the number explicable rather than arbitrary, and this is the
  // only place in the editor that says what a saved-site limit even is.
  return `${where} · ${String(count)} of ${String(projectLimit)} sites on ${name}`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown date';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
